import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { AttachmentOwnerInput } from "../../../clients/graphqlTypes.js";
import {
  AttachmentDownloadUrlDocument,
  AttachmentDownloadUrlQuery,
  AttachmentDownloadUrlQueryVariables,
} from "./useOpenAttachment.generated.js";
import {
  useOpenFile,
  type OpenFileOptions,
} from "../../commons/hooks/useOpenFile.js";

export class AttachmentUrlUnavailableError extends Error {
  constructor(reason: string) {
    super(`attachment download URL unavailable: ${reason}`);
    this.name = "AttachmentUrlUnavailableError";
  }
}

export type OpenAttachmentOptions = OpenFileOptions & {
  as: "pdf" | "any";
};

/**
 * Opens a stored document file (invoice, purchase order, time report, saved
 * quote document) with a download URL signed at click time. Never open the
 * owner's cached `url` fields: they expire one hour after the page loaded.
 */
export const useOpenAttachment = () => {
  const client = useApolloClient();
  const { openPdf, openAnyFile } = useOpenFile();

  const resolveAttachmentUrl =
    (owner: AttachmentOwnerInput) => async (): Promise<string> => {
      const { data } = await client.query<
        AttachmentDownloadUrlQuery,
        AttachmentDownloadUrlQueryVariables
      >({
        query: AttachmentDownloadUrlDocument,
        variables: { owner },
        // keep the short-lived URL out of the normalised cache
        fetchPolicy: "no-cache",
      });
      const result = getDataOrNull(data?.attachmentDownloadUrl);
      if (!result) {
        throw new AttachmentUrlUnavailableError(
          data?.attachmentDownloadUrl?.__typename ?? "no data"
        );
      }
      return result.url;
    };

  const openAttachment = (
    owner: AttachmentOwnerInput,
    fileName: string,
    { as, filenameFromResponse }: OpenAttachmentOptions
  ) => {
    const open = as === "pdf" ? openPdf : openAnyFile;
    return open(resolveAttachmentUrl(owner), fileName, {
      filenameFromResponse,
    });
  };

  return { openAttachment, resolveAttachmentUrl };
};
